import React, { useState } from 'react';

interface PieData {
  name: string;
  value: number;
  color: string;
}

interface Props {
  data: PieData[];
  size?: number;
  showLegend?: boolean;
  title?: string;
  isDark?: boolean;
}

const AnalyticsPieCard: React.FC<Props> = ({
  data,
  size = 200,
  showLegend = true,
  title,
  isDark = false
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const total = data.reduce((sum, item) => sum + item.value, 0) || 0;
  
  const radius = 40;
  const strokeWidth = 12;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;

  let cumulativeOffset = 0;

  return (
    <div className={`p-6 transition-colors duration-300`}>
      {title && (
        <h4 className={`text-lg font-bold mb-6 ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>
          {title}
        </h4>
      )}

      <div className="relative flex justify-center items-center">
        {/* SVG dirotasi -90deg agar mulai dari jam 12 */}
        <svg height={size} width={size} viewBox="0 0 100 100" className="transform -rotate-90">
          {/* Background circle */}
          <circle cx="50" cy="50" r={normalizedRadius} strokeWidth={strokeWidth} 
                  stroke={isDark ? '#334155' : '#e2e8f0'} fill="transparent" />
          
          {data.map((slice, index) => {
            const percent = slice.value / total;
            const strokeDasharray = `${percent * circumference} ${circumference}`;
            const currentOffset = cumulativeOffset;
            cumulativeOffset += percent * circumference;

            return (
              <circle
                key={index}
                cx="50"
                cy="50"
                r={normalizedRadius}
                fill="transparent"
                stroke={slice.color}
                strokeWidth={strokeWidth}
                strokeDasharray={strokeDasharray}
                strokeDashoffset={-currentOffset}
                strokeLinecap="round"
                className="cursor-pointer transition-all duration-300 ease-in-out"
                style={{
                  opacity: hoveredIndex === null || hoveredIndex === index ? 1 : 0.2,
                  scale: hoveredIndex === index ? 1.05 : 1,
                  transformOrigin: '50% 50%'
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            );
          })}
          
          {/* Teks di tengah */}
          <text 
            x="50" y="50" 
            className="gauge-value"
            fill={isDark ? '#f1f5f9' : '#1e293b'}
            fontSize="12" 
            textAnchor="middle" 
            dominantBaseline="middle"
          >
            {hoveredIndex !== null ? `${data[hoveredIndex].value}%` : `${total}%`}
          </text>
        </svg>

        {/* Custom Tooltip */}
        {hoveredIndex !== null && (
          <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none bg-slate-900 text-xs px-3 py-1.5 rounded-full shadow-xl animate-in fade-in zoom-in duration-200 text-${isDark ? 'white' : 'black'}`}>
            {data[hoveredIndex].name}
          </div>
        )}
      </div>

      {showLegend && (
        <div className="mt-8 grid gap-3">
          {data.map((item, index) => (
            <div 
              key={index} 
              className={`flex items-center justify-between text-sm p-2 rounded-lg transition-colors ${hoveredIndex === index ? (isDark ? 'bg-slate-700' : 'bg-slate-50') : ''}`}
            >
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: item.color }} />
                <span className={isDark ? 'text-slate-300' : 'text-slate-600'}>{item.name}</span>
              </div>
              <span className={`font-mono font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {item.value}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnalyticsPieCard;